import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { Button } from "@mantine/core";
import { IconRefresh } from "@tabler/icons";
import { hardReloadClient } from "../utils/clientReload";
import { isRemoteVersionNewer, isTrustedRemoteVersion } from "../utils/versionCompare";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const CURRENT_VERSION = process.env.REACT_APP_VERSION;
const IS_DEV = process.env.NODE_ENV === "development";

let snapshot = {
  updateAvailable: false,
  latestVersion: null,
};

const listeners = new Set();
let pollTimer = null;
let subscriberCount = 0;
let notifiedVersion = null;

function emit() {
  listeners.forEach((listener) => listener());
}

function setSnapshot(next) {
  snapshot = next;
  emit();
}

async function checkVersion() {
  if (IS_DEV) return;

  try {
    const base = process.env.PUBLIC_URL || "";
    const res = await fetch(`${base}/version.json?ts=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return;

    const data = await res.json();
    const latest = data?.version;
    const updateAvailable = isRemoteVersionNewer(latest, CURRENT_VERSION);

    setSnapshot({
      updateAvailable,
      latestVersion: isTrustedRemoteVersion(latest) ? latest : null,
    });
  } catch {
    // Silent failure — do not disturb users on network errors.
  }
}

function startPolling() {
  if (pollTimer || IS_DEV) return;
  checkVersion();
  pollTimer = window.setInterval(checkVersion, POLL_INTERVAL_MS);
}

function stopPolling() {
  if (!pollTimer) return;
  window.clearInterval(pollTimer);
  pollTimer = null;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

export function useClientVersionCheck({ notify = false } = {}) {
  const { t, ready } = useTranslation();
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    subscriberCount += 1;
    startPolling();

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      subscriberCount -= 1;
      document.removeEventListener("visibilitychange", onVisible);
      if (subscriberCount === 0) {
        stopPolling();
      }
    };
  }, []);

  useEffect(() => {
    if (!ready || !notify || !state.updateAvailable || !state.latestVersion) {
      return;
    }
    if (notifiedVersion === state.latestVersion) {
      return;
    }

    notifiedVersion = state.latestVersion;

    showNotification({
      id: "client-update-available",
      title: t("app.updateAvailable.title", {
        defaultValue: "Update available",
      }),
      message: (
        <>
          <div style={{ marginBottom: 8 }}>
            {t("app.updateAvailable.message", {
              latest: state.latestVersion,
              current: CURRENT_VERSION,
              defaultValue: `A new version (${state.latestVersion}) is available. You are on ${CURRENT_VERSION}. Please reload to get the latest changes.`,
            })}
          </div>
          <Button
            size="xs"
            leftIcon={<IconRefresh size={14} />}
            onClick={hardReloadClient}
          >
            {t("app.updateAvailable.reload", {
              defaultValue: "Reload now",
            })}
          </Button>
        </>
      ),
      autoClose: false,
      color: "orange",
      withCloseButton: true,
    });
  }, [ready, notify, state.updateAvailable, state.latestVersion, t]);

  return {
    updateAvailable: state.updateAvailable,
    latestVersion: state.latestVersion,
    reload: hardReloadClient,
  };
}
